import { formatClockTime } from "../../utils/scheduledTimes";
import { TypeIcon } from "../common/TypeIcon";
import React, { useMemo, useState } from "react";
import { flightRoute, flightRouteLabel, titleWithRoute } from "../../utils/flightRoute";
import { Link } from "react-router-dom";
import type { Interaction, LoungeRound, TypeSettings } from "../../types";
import { getInteractionEntityType } from "../../types";
import { useAllTypeSettings } from "../../contexts/TypeSettingsContext";
import { typeCustomQuestions } from "../../config/typeSettings";
import type { EntityInfo } from "../../hooks/useEntityMap";
import { NotesDisplay } from "../common/NotesDisplay";
import { useStayPosition } from "../../hooks/useHotelStays";
import { answeredQuestions } from "../../utils/customQuestions";
import { buildActivityForest, descendantActivityIds } from "../../utils/activityTree";
import { formatDayKey } from "../../utils/calendarDay";
import {
  calcOrderToReceivedTime,
  calcTotalDelayTime,
  calcTotalWaitTime,
  interactionTimelineEvents,
  eventZone,
  expectedExpressWait,
  formatDateLabel,
  formatDuration,
  formatEventType,
  formatTime,
  formatZonedTimeRange,
  isActivityActive,
  lastEventZone,
  orderActivities,
  zoneAbbrev,
  zoneNote,
  type SegmentDayLookup,
  type SegmentZoneLookup,
  type Zone,
} from "../../utils/tripDisplay";

type ActivityItem = Interaction & { id: string };

interface ActivityListProps {
  activities: ActivityItem[];
  entityMap: Record<string, string>;
  entityDataMap: Record<string, EntityInfo>;
  tripId: string;
  /** "asc" = oldest → newest (default), "desc" = newest first. */
  sortDir?: "asc" | "desc";
  /**
   * Resolves the day of an activity that has no events of its own, so a
   * backfilled one groups under the day it happened rather than the day it was
   * imported. Build it with `segmentDayLookup(segments)`.
   */
  onDay?: SegmentDayLookup;
  /**
   * The fallback zone for activities and events logged before times carried
   * their own zone. Build it with `segmentZoneLookup(segments, trip.timezone)`.
   */
  onZone?: SegmentZoneLookup;
}

interface ActivityRow {
  interaction: ActivityItem;
  start: Date | null;
  end: Date | null;
  activityDate: Date | null;
  bucketKey: string | null;
  /** Where the activity started — its day and its start time read here. */
  zone: Zone;
  /** Where its last event was logged — the far end of its time range. */
  endZone: Zone;
}

/** Everything the rows need that doesn't change per row. */
interface RenderContext {
  /** Saved settings per type — a place without questions asks its type's. */
  typeSettings: Record<string, TypeSettings>;
  entityMap: Record<string, string>;
  entityDataMap: Record<string, EntityInfo>;
  tripId: string;
  sortDir: "asc" | "desc";
  onDay?: SegmentDayLookup;
  onZone?: SegmentZoneLookup;
  /** parentId → the activities logged inside it (see `utils/activityTree`). */
  childrenOf: Map<string, ActivityItem[]>;
  /** How many activities sit beneath each one in total, nesting included. */
  nestedTotals: Map<string, number>;
  expandedIds: Set<string>;
  toggleExpand: (id: string, e: React.MouseEvent) => void;
  collapsedNests: Set<string>;
  toggleNest: (id: string) => void;
}

function toRows(activities: ActivityItem[], ctx: RenderContext): ActivityRow[] {
  return orderActivities(activities, ctx.onDay, ctx.sortDir, ctx.onZone).map(
    ({ item, day, dayKey, zone, start, end }) => ({
      interaction: item,
      start,
      end,
      activityDate: day,
      bucketKey: dayKey,
      zone,
      endZone: lastEventZone(item, ctx.onZone),
    }),
  );
}

export function ActivityList({
  activities,
  entityMap,
  entityDataMap,
  tripId,
  sortDir = "asc",
  onDay,
  onZone,
}: ActivityListProps) {
  const typeSettings = useAllTypeSettings();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Nests start open — on a trip, what you did at the place is the point of
  // having logged the place. Collapsing is per-row and remembered while the
  // list is mounted, for reading back a long day.
  const [collapsedNests, setCollapsedNests] = useState<Set<string>>(new Set());

  // Activities logged under other activities are drawn inside them. Anything
  // whose parent isn't in this list (a filtered view, a segment block holding
  // only part of the tree) is drawn at the top level rather than vanishing.
  const { roots, childrenOf } = useMemo(() => buildActivityForest(activities), [activities]);
  const nestedTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const parentId of childrenOf.keys()) {
      totals.set(parentId, descendantActivityIds(activities, parentId).length);
    }
    return totals;
  }, [activities, childrenOf]);

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleNest = (id: string) => {
    setCollapsedNests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ctx: RenderContext = {
    typeSettings,
    entityMap,
    entityDataMap,
    tripId,
    sortDir,
    onDay,
    onZone,
    childrenOf,
    nestedTotals,
    expandedIds,
    toggleExpand,
    collapsedNests,
    toggleNest,
  };

  // Sort + derive once per change in the roots. Each row carries its
  // pre-computed time range so the render pass doesn't recompute it.
  const rows = useMemo(
    () => toRows(roots, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roots, sortDir, onDay, onZone],
  );

  let lastBucketKey: string | null = null;
  let dayZone: Zone = undefined;

  return (
    <div className="tl">
      {rows.map((row, idx) => {
        const showDateSeparator = row.bucketKey !== null && row.bucketKey !== lastBucketKey;
        if (row.bucketKey) lastBucketKey = row.bucketKey;
        if (showDateSeparator) dayZone = row.zone;

        return (
          <React.Fragment key={row.interaction.id}>
            {showDateSeparator && row.activityDate && (
              <div className="tl-date-separator">
                <span className="tl-date-label">
                  {row.bucketKey
                    ? formatDayKey(row.bucketKey, { weekday: "short", month: "short", day: "numeric" })
                    : formatDateLabel(row.activityDate, row.zone)}
                </span>
                {/* Named once per day rather than on every row: most days run
                    on one clock, and repeating it would be noise. A row on a
                    different clock (the travel day) labels itself. */}
                {zoneNote(row.activityDate, row.zone) && (
                  <span className="tl-date-zone">{zoneNote(row.activityDate, row.zone)}</span>
                )}
              </div>
            )}
            <ActivityRow row={row} isLast={idx === rows.length - 1} ctx={ctx} dayZone={dayZone} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * One activity, plus anything logged inside it. Days are marked at the top
 * level only — a nest belongs to the row above it, so repeating the date inside
 * would break the place apart rather than hold it together.
 */
function ActivityRow({
  row,
  isLast,
  ctx,
  dayZone,
}: {
  row: ActivityRow;
  isLast: boolean;
  ctx: RenderContext;
  /** The zone the day's separator was drawn for; rows off it label themselves. */
  dayZone?: Zone;
}) {
  const { interaction, start, end, zone, endZone } = row;
  // A hotel visit says which of its stay's visits it is, and leads to the stay.
  const stayPosition = useStayPosition(interaction.id);
  let timeRange = formatZonedTimeRange(start, zone, end, endZone);
  if (timeRange && start && (zone ?? null) === (endZone ?? null) && (zone ?? null) !== (dayZone ?? zone ?? null)) {
    const abbrev = zoneAbbrev(start, zone);
    if (abbrev) timeRange = `${timeRange} ${abbrev}`;
  }
  const created = interaction.createdAt?.toDate();
  const entityInfo = interaction.entityId ? ctx.entityDataMap[interaction.entityId] : null;
  const displayName = titleWithRoute(
    interaction.name ||
      (interaction.entityId && ctx.entityMap[interaction.entityId]) ||
      "Unnamed Activity",
    flightRouteLabel(
      flightRoute(interaction.customResponses as Record<string, unknown> | undefined),
      (id) => ctx.entityMap[id],
    ),
  );
  // Events with round placed/received times projected in (read-only),
  // so round timing shows in the mini-timeline and feeds the metrics.
  const evs = interactionTimelineEvents(
    interaction.events,
    (interaction.customResponses as Record<string, unknown>)?.rounds as LoungeRound[] | undefined,
  );
  const eventCount = evs.length;
  const isExpanded = ctx.expandedIds.has(interaction.id);
  const isActive = isActivityActive(
    interaction.events,
    getInteractionEntityType(entityInfo?.type ? { type: entityInfo.type } : null, interaction.customResponses as Record<string, unknown>),
    interaction.customResponses as Record<string, unknown>,
  );

  const children = ctx.childrenOf.get(interaction.id) ?? [];
  const nestedTotal = ctx.nestedTotals.get(interaction.id) ?? 0;
  const nestOpen = children.length > 0 && !ctx.collapsedNests.has(interaction.id);
  const childRows = nestOpen ? toRows(children, ctx) : [];

  return (
    <div
      className={`tl-item ${isLast && !nestOpen ? "tl-item-last" : ""} ${isActive ? "tl-item-active" : ""} ${children.length > 0 ? "tl-item-nested-parent" : ""}`}
    >
      <div className="tl-marker">
        {entityInfo?.imageUrl ? (
          <img src={entityInfo.imageUrl} alt="" className="tl-marker-img" />
        ) : (
          <div className="tl-marker-icon">
            <TypeIcon type={entityInfo?.type} name={entityInfo?.displayName} tile />
          </div>
        )}
        {isActive && <span className="tl-marker-pulse" />}
      </div>

      <div className={`tl-content ${isExpanded ? "tl-content-expanded" : ""}`}>
        <div className="tl-row">
          <div className="tl-info">
            <Link to={`/trips/${ctx.tripId}/interactions/${interaction.id}`} className="tl-name">
              {displayName}
              {isActive && <span className="tl-active-badge">LIVE</span>}
            </Link>
            <div className="tl-meta">
              {timeRange ? (
                <span className="tl-time">{timeRange}</span>
              ) : created ? (
                <span className="tl-time tl-time-muted">
                  {created.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: zone })}
                </span>
              ) : null}
              {stayPosition && stayPosition.count > 1 && (
                <Link
                  to={`/trips/${ctx.tripId}/stays/${stayPosition.stay.id}`}
                  className="tl-badge tl-badge-stay"
                >
                  Stay · visit {stayPosition.index} of {stayPosition.count}
                </Link>
              )}
              {(() => {
                const tierName = interaction.accessTierId
                  ? entityInfo?.activityOptions?.find(
                      (o) => o.id === interaction.accessTierId,
                    )?.displayName ?? interaction.accessTierId
                  : null;
                const expected = expectedExpressWait(interaction.postedWait, tierName);
                return (
                  <>
                    {tierName && <span className="tl-badge">{tierName}</span>}
                    {interaction.postedWait != null && interaction.postedWait > 0 && (
                      <span className="tl-wait">Posted: {interaction.postedWait}m</span>
                    )}
                    {expected != null && (
                      <span className="tl-wait">Expected: {expected}m</span>
                    )}
                  </>
                );
              })()}
              {(() => {
                const cr = interaction.customResponses as Record<string, unknown> | undefined;
                const st = cr?.scheduled_time;
                const boards = cr?.scheduled_boarding;
                // A flight's departure sits beside its boarding time. Its
                // route is in the title.
                const route = flightRoute(cr);
                if (boards || route.from || route.to) {
                  return (
                    <>
                      {boards ? <span className="tl-wait">Boards: {formatClockTime(boards)}</span> : null}
                      {st ? <span className="tl-wait">Departs: {formatClockTime(st)}</span> : null}
                    </>
                  );
                }
                return st ? <span className="tl-wait">Sched: {formatClockTime(st)}</span> : null;
              })()}
              {(() => {
                const loc = (interaction.customResponses as Record<string, unknown>)?.location;
                return typeof loc === "string" && loc.trim() ? (
                  <span className="tl-badge tl-badge-loc">📍 {loc}</span>
                ) : null;
              })()}
              {answeredQuestions(
                entityInfo?.customQuestions?.length
                  ? entityInfo.customQuestions
                  : typeCustomQuestions(
                      ctx.typeSettings[
                        getInteractionEntityType(entityInfo?.type ? { type: entityInfo.type } : null, interaction.customResponses as Record<string, unknown>) ?? ""
                      ],
                    ),
                interaction.customResponses,
              ).map(
                ({ question, value }) => (
                  <span key={question.id} className="tl-badge tl-badge-answer">
                    {question.label}: {value}
                  </span>
                ),
              )}
              {(() => {
                const tw = calcTotalWaitTime(evs);
                const otr = calcOrderToReceivedTime(evs);
                const td = calcTotalDelayTime(evs);
                return (
                  <>
                    {tw > 0 && <span className="tl-wait">Wait: {formatDuration(tw)}</span>}
                    {otr > 0 && <span className="tl-wait">Food: {formatDuration(otr)}</span>}
                    {td > 0 && <span className="tl-wait tl-delay">Delay: {formatDuration(td)}</span>}
                  </>
                );
              })()}
            </div>
          </div>
          {children.length > 0 && (
            <button
              type="button"
              className={`tl-expand tl-nest-toggle ${nestOpen ? "tl-expand-open" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                ctx.toggleNest(interaction.id);
              }}
              title={
                nestOpen
                  ? `Hide the ${nestedTotal} activit${nestedTotal === 1 ? "y" : "ies"} logged here`
                  : `Show the ${nestedTotal} activit${nestedTotal === 1 ? "y" : "ies"} logged here`
              }
              aria-expanded={nestOpen}
            >
              <span className="tl-expand-count">{nestedTotal}</span>
              <span className="tl-nest-word">inside</span>
              <svg className="tl-expand-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
          {(eventCount > 0 || interaction.notes) && (
            <button
              className={`tl-expand ${isExpanded ? "tl-expand-open" : ""}`}
              onClick={(e) => ctx.toggleExpand(interaction.id, e)}
              title={isExpanded ? "Collapse" : `${eventCount} events`}
            >
              {eventCount > 0 && <span className="tl-expand-count">{eventCount}</span>}
              <svg className="tl-expand-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>

        {isExpanded && (eventCount > 0 || interaction.notes) && (
          <div className="tl-events">
            {interaction.notes && <NotesDisplay value={interaction.notes} className="tl-notes" />}
            {[...evs]
              .sort((a, b) => a.eventTimestamp.toDate().getTime() - b.eventTimestamp.toDate().getTime())
              .map((event, i) => (
                <div key={i} className="tl-event">
                  <span className="tl-event-time">
                    {(() => {
                      // Each step on the clock it was logged on, labeled only
                      // when this activity spans more than one.
                      const at = event.eventTimestamp.toDate();
                      const z = eventZone(event, interaction, ctx.onZone);
                      const abbrev = (zone ?? null) !== (endZone ?? null) ? zoneAbbrev(at, z) : null;
                      return abbrev ? `${formatTime(at, z)} ${abbrev}` : formatTime(at, z);
                    })()}
                  </span>
                  <span className="tl-event-dot" />
                  <span className="tl-event-label">
                    {event.customLabel || formatEventType(event.eventType)}
                  </span>
                </div>
              ))}
            <Link
              to={`/trips/${ctx.tripId}/interactions/${interaction.id}`}
              className="tl-events-edit"
            >
              Edit activity &rarr;
            </Link>
          </div>
        )}

        {nestOpen && (
          <div className="tl tl-nest">
            {childRows.map((childRow, i) => (
              <ActivityRow
                key={childRow.interaction.id}
                row={childRow}
                isLast={i === childRows.length - 1}
                ctx={ctx}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
